// useKstTime composable — KST/UTC 시간대 변환 및 sessionStorage 영속 (ux-patterns.md §12.4)
import { ref } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';

// @MX:SPEC: SPEC-ECOUNT-ETL-OPS-CONSOLE-001 REQ-OC-COMMON-004 (design.md §7)

dayjs.extend(utc);
dayjs.extend(relativeTime);

/**
 * 시간대 변환 composable
 * @param {Object} options
 * @param {string} options.storageKey - sessionStorage 키 (기본 'ecountEtl.tz')
 * @returns {{ currentTz, toggleTz, format, toKST, toUTC, formatRelative }}
 */
export function useKstTime(options = {}) {
  const { storageKey = 'ecountEtl.tz' } = options;

  const stored = sessionStorage.getItem(storageKey);
  const currentTz = ref(stored === 'utc' ? 'utc' : 'kst');

  function toggleTz() {
    currentTz.value = currentTz.value === 'kst' ? 'utc' : 'kst';
    sessionStorage.setItem(storageKey, currentTz.value);
  }

  /**
   * KST 강제 변환 (YYYY-MM-DD HH:mm:ss KST)
   */
  function toKST(value) {
    if (!value) return '';
    return dayjs(value).utcOffset('+09:00').format('YYYY-MM-DD HH:mm:ss') + ' KST';
  }

  /**
   * UTC 강제 변환 (YYYY-MM-DD HH:mm:ss UTC)
   */
  function toUTC(value) {
    if (!value) return '';
    return dayjs(value).utc().format('YYYY-MM-DD HH:mm:ss') + ' UTC';
  }

  /**
   * 현재 설정 시간대로 포맷
   * @param {string|Date|null} value
   * @param {Object} opts - { showLabel: boolean }
   */
  function format(value, opts = {}) {
    const { showLabel = true } = opts;
    if (!value) return '';
    if (currentTz.value === 'utc') {
      return showLabel ? toUTC(value) : dayjs(value).utc().format('YYYY-MM-DD HH:mm:ss');
    }
    return showLabel ? toKST(value) : dayjs(value).utcOffset('+09:00').format('YYYY-MM-DD HH:mm:ss');
  }

  /**
   * 상대 시간 (예: "2분 전")
   */
  function formatRelative(value) {
    if (!value) return '';
    return dayjs(value).fromNow();
  }

  return { currentTz, toggleTz, format, toKST, toUTC, formatRelative };
}
